/**
 * DeVersionCompareLauncher — "Compare: [version…] [Compare]" for a BfArM label
 * (Fachinformation / Gebrauchsinformation). Opens the EU two-pane compare bound
 * to `/de_pdf`: red/green is the diff of the stored GERMAN text; English is a
 * reading aid on hover (a line, or a whole table as a grid) — live, cached,
 * never diffed. Renders nothing unless the label has >= 2 versions.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { dePdfApi } from '../../api/createViewerApi';
import { authApi } from '../../services/authApi';
import EuHistoricalCompare, { type CompareSource } from './EuHistoricalCompare';

interface Ver { vstr: string; date: string; docid: string }

// ── hover translate: one region's English on demand (never the whole page) ──
type Box = [number, number, number, number];
type Region = { bbox: Box; text: string; table: boolean };   // table: text is the <table> HTML
const regionsByDoc = new Map<number, Promise<Map<number, Region[]>>>();
const enByText = new Map<string, Promise<string>>();

function docRegions(docId: number): Promise<Map<number, Region[]>> {
  let p = regionsByDoc.get(docId);
  if (!p) {
    p = (async () => {
      const out = new Map<number, Region[]>();
      for (let from = 1; from < 2000; from += 50) {       // 50 pages per batch (endpoint cap)
        const r = await dePdfApi.fetchBatchRegions(docId, from, from + 49);
        const got = Object.entries(r.pages || {});
        for (const [pg, v] of got) out.set(Number(pg), [
          ...(v.line_regions || []).map((r) => ({ bbox: r.bbox as Box, text: r.text_src || '', table: false })),
          ...(v.table_regions || []).map((t) => ({ bbox: t.bbox as Box, text: t.html_ja || '', table: true })),
        ]);
        if (got.length < 50) break;
      }
      return out;
    })();
    p.catch(() => regionsByDoc.delete(docId));
    regionsByDoc.set(docId, p);
  }
  return p;
}

function english(text: string, html: boolean, docId: number): Promise<string> {
  const key = `${html ? 'h' : 't'}:${text}`;
  let p = enByText.get(key);
  if (!p) {
    p = authApi.translateDePdfText(text, html, docId, null);   // html: the grid survives
    p.catch(() => enByText.delete(key));
    enByText.set(key, p);
  }
  return p;
}

export function HoverTranslateLayer({ docId, page }: { docId: number; page: number }) {
  const [regions, setRegions] = useState<Region[]>([]);
  const [hover, setHover] = useState<number | null>(null);
  const [en, setEn] = useState<string>('');
  const timer = useRef<number>(0);

  useEffect(() => {
    let alive = true;
    docRegions(docId).then((m) => { if (alive) setRegions(m.get(page) || []); }).catch(() => {});
    return () => { alive = false; };
  }, [docId, page]);

  const enter = (i: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {      // a pass-over is not a request
      setHover(i); setEn('');
      const r = regions[i];
      if (r?.text) english(r.text, r.table, docId).then((x) => setEn(x)).catch(() => setEn('Translation unavailable.'));
    }, 250);
  };
  const leave = () => { window.clearTimeout(timer.current); setHover(null); };
  const h = hover != null ? regions[hover] : undefined;

  return (
    <>
      {regions.map((r, i) => (
        <div key={i} onMouseEnter={() => enter(i)} onMouseLeave={leave}
             className={`absolute rounded-sm ${hover === i ? (r.table ? 'ring-1 ring-emerald-500 bg-emerald-500/10'
                                                                 : 'ring-1 ring-indigo-400 bg-indigo-400/10') : ''}`}
             style={{ left: `${r.bbox[0] * 100}%`, top: `${r.bbox[1] * 100}%`,
                      width: `${(r.bbox[2] - r.bbox[0]) * 100}%`, height: `${(r.bbox[3] - r.bbox[1]) * 100}%` }} />
      ))}
      {h && (
        // ponytail: a tall table near mid-page can overflow the page box; clamp/portal if users hit it.
        <div className={`absolute z-30 rounded-md bg-white px-2.5 py-1.5 text-slate-700 shadow-lg ring-1 ring-slate-200 pointer-events-none
                         ${h.table ? 'max-w-[96%] text-[11px] [&_table]:border-collapse [&_td]:border [&_td]:border-slate-200 [&_td]:px-1 [&_th]:border [&_th]:border-slate-200 [&_th]:px-1'
                                   : 'max-w-[70%] text-[12px] leading-snug'}`}
             style={{ left: `${Math.min(h.bbox[0], h.table ? 0.02 : 0.3) * 100}%`,
                      ...(h.bbox[3] > (h.table ? 0.6 : 0.8) ? { bottom: `${(1 - h.bbox[1]) * 100 + 0.5}%` }
                                                            : { top: `${(h.bbox[3] + 0.005) * 100}%` }) }}>
          {!en ? <span className="text-slate-400">Translating…</span>
            : h.table ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(en) }} /> : en}
        </div>
      )}
    </>
  );
}

export default function DeVersionCompareLauncher({ docId, docType, brand }: {
  docId: number; docType: string; brand: string;
}) {
  const [versions, setVersions] = useState<Ver[]>([]);
  const [sel, setSel] = useState('');
  const [open, setOpen] = useState(false);
  const label = docType === 'pil' ? 'PIL' : 'SmPC';

  useEffect(() => {
    let alive = true;
    setVersions([]); setSel('');
    authApi.getDePdfVersions(docId)
      .then((r) => { if (alive) setVersions(r.versions || []); })
      .catch(() => { /* compare optional — stay hidden */ });
    return () => { alive = false; };
  }, [docId]);

  // Stable identity: the compare refetches when ``source`` changes.
  const source = useMemo<CompareSource>(() => ({
    versionsPath: `/de_pdf/documents/${docId}/versions`,
    diffPath: (o, n) => `/de_pdf/compare?old=${encodeURIComponent(o)}&new=${encodeURIComponent(n)}`,
    docLabel: label,
    subtitle: `BfArM ${label === 'PIL' ? 'Gebrauchsinformation' : 'Fachinformation'} versions`,
    pageOverlay: (side, vstr, page) => <HoverTranslateLayer key={`${side}-${vstr}-${page}`} docId={Number(vstr)} page={page} />,
  }), [docId, label]);

  if (versions.length < 2) return null;
  const latest = versions[versions.length - 1]!.vstr;
  const options = versions.map((v, i) => ({ ...v, ord: i + 1 })).filter((o) => o.vstr !== latest).reverse();

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[13px] text-ink-2">Compare:</span>
      <select value={sel} onChange={(e) => setSel(e.target.value)}
              className="h-9 min-w-[220px] rounded-ctl border border-line-input bg-white px-2 text-sm text-ink focus:outline-none focus:ring-4 focus:ring-accent-ring">
        <option value="">Select version…</option>
        {options.map((o) => (
          <option key={o.vstr} value={o.vstr}>{label} - V{o.ord} - {o.date}</option>
        ))}
      </select>
      <button disabled={!sel} onClick={() => setOpen(true)} className="btn-secondary disabled:opacity-50">
        Compare
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-[1700px] max-h-[93vh] overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <EuHistoricalCompare hc={String(docId)} brand={brand} source={source}
                                 initialOld={sel} initialNew={latest} onClose={() => setOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
